import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3ddefcf7"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "tab-panel" }
const _hoisted_2 = { class: "smaller-fields" }
const _hoisted_3 = { class: "tab-panel" }
const _hoisted_4 = { class: "message-character-fields" }

import { computed, ref, inject, watch } from "vue";
import modalBase from "@/components/ui/modals/modal-base.vue";
import { ModalWidth } from "@/types/ui/modals/ModalWidth";
import { DxToolbarItem } from "devextreme-vue/popup";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { ValidatedEvent } from "devextreme/ui/validator";
import { FieldSize } from "@/enums/system/FieldSize";
import SwitchToggle from "./ui/toggles/switch-toggle.vue";
import AcquaintTextbox from "@/components/ui/inputs/acquaint-textbox.vue";
import { SMSTextsValidation } from "acquaint-api/validation/SMSTextsValidation";
import { SMSTexts } from "acquaint-api/models/SMSTexts";
import NumberDropdown from "./ui/dropdowns/number-dropdown.vue";
import { CodeType } from "acquaint-api/constants/CodeType";
import SystemCodeSingleSelect from "./ui/system-codes/system-code-single-select.vue";
import AcquaintInfoBox from "./ui/inputs/acquaint-info-box.vue";
import { DxTabPanel, DxItem } from "devextreme-vue/tab-panel";
import AcquaintTextArea from "./ui/inputs/acquaint-text-area.vue";
import { LookupTableSource } from "acquaint-api/constants/LookupTableSource";
import LookupSingleSelect from "./ui/lookup/lookup-single-select.vue";
import MergeFieldsModal from "./contacts/merge-fields-modal.vue";
import AiTextGeneratorModal from "./ai/ai-text-generator-modal.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'new-edit-sms-texts-templates-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /** Id of the SMS Texts templates */
  configurationId: {
    type: Number,
    required: true,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /** Triggered when the SMS Texts template has been updated*/
  "configurationItemEdited",
  /** Triggered when the SMS Texts template has been created*/
  "configurationItemCreated",
],
  setup(__props, { emit }) {

const props = __props;



const displayCloseConfirmationPrompt = ref(false);
const originalItemData = ref<SMSTexts>();
const itemData = ref<SMSTexts>();
const lockedByAnotherUser = ref(false);
const keepingModalOpening = ref(false);
const acquaintApiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const submissionResponse = ref<SMSTexts>();
const selectedTab = ref(0);
const openMergeFieldsModal = ref(false);
const characterCount = ref(0);
const displayAIModal = ref(false);



const showModal = computed({
  get: () => props.modelValue,
  set: (value: Boolean) => emit("update:modelValue", value),
});

const lockConfigurationItem = async () => {
  try {
    await acquaintApiClient.httpPostVoid(
      `database/smstexts/${props.configurationId}/lock`,
      { lock: true, extraInfo: "" },
    );
  } catch (err) {
    lockedByAnotherUser.value = true;
    displayErrorMessage(
      "These details are already being edited by another user.",
    );
  }
};

const unLockConfigurationItem = async () => {
  try {
    await acquaintApiClient.httpPostVoid(
      `database/smstexts/${props.configurationId}/lock`,
      { lock: false, extraInfo: "" },
    );
  } catch (err) {
    displayErrorMessage("We were unable to unlock these details.");
  }
};

const fetchItemData = async () => {
  try {
    if (props.configurationId == 0) {
      originalItemData.value = await acquaintApiClient.httpGet<SMSTexts>(
        `database/smstexts/defaultmodel`,
      );
    } else {
      originalItemData.value = await acquaintApiClient.httpGet<SMSTexts>(
        `database/smstexts/${props.configurationId}`,
      );
    }
  } catch (err) {
    itemData.value = undefined;
    originalItemData.value = undefined;
    if (props.configurationId == 0) {
      displayErrorMessage("The selected details could not be fetched");
    } else {
      displayErrorMessage("We could not fetch the default data");
    }
  }
};

watch(
  () => itemData.value,
  () => {
    displayCloseConfirmationPrompt.value =
      JSON.stringify(itemData.value) != JSON.stringify(originalItemData.value);
  },
  { deep: true },
);

watch(
  () => showModal.value,
  async () => {
    if (keepingModalOpening.value) {
      return;
    }

    if (showModal.value) {
      lockedByAnotherUser.value = false;
      selectedTab.value = 0;
      characterCount.value = 0;
      displayCloseConfirmationPrompt.value = false;

      await fetchItemData();
      if (originalItemData.value) {
        itemData.value = JSON.parse(JSON.stringify(originalItemData.value));
      }

      if (props.configurationId == 0 || !itemData.value) {
        return;
      }

      /* As new lines from the api contain \r\n due to the api being hosted in a Windows environment
       * We must replace them with \n, otherwise the character count gets out of sync */
      itemData.value.body = itemData.value.body.replace(/\r\n/g, "\n");

      await lockConfigurationItem();
    }
  },
);

const saveDetails = async (e: any) => {
  const validation: ValidatedEvent = e.validationGroup.validate();
  // Don't continue if user has entered invalid data
  if (!validation.isValid) {
    return;
  }

  if (!(await callSaveApi())) {
    return;
  }

  displayCloseConfirmationPrompt.value = false;
  lockedByAnotherUser.value = true;

  if (props.configurationId > 0) {
    await unLockConfigurationItem();
    emit("configurationItemEdited", itemData.value?.inActive);
  } else {
    emit("configurationItemCreated");
  }

  showModal.value = false;
};

const callSaveApi = async () => {
  try {
    if (props.configurationId == 0) {
      submissionResponse.value = await acquaintApiClient.httpPost<SMSTexts>(
        `database/smstexts`,
        itemData.value,
      );
    } else {
      submissionResponse.value = await acquaintApiClient.httpPut<SMSTexts>(
        `database/smstexts/${props.configurationId}`,
        itemData.value,
      );
    }
    return true;
  } catch (err) {
    if (props.configurationId == 0) {
      displayErrorMessage("A new system item could not be created.");
    } else {
      displayErrorMessage("The selected details could not be edited.");
    }
    return false;
  }
};
const cancelClick = () => {
  showModal.value = false;
};

const keepEnteredInformation = () => {
  keepingModalOpening.value = true;
};

const cancelPromptDenied = () => {
  keepingModalOpening.value = true;
};

const modalClosed = async () => {
  if (props.configurationId > 0) {
    await unLockConfigurationItem();
  }
  keepingModalOpening.value = false;
};

const messageLengthWarning = computed(() => {
  return itemData.value && itemData.value.body.length > 160
    ? "Will be sent as multiple texts"
    : "";
});

const addMergeField = (mergeField: string) => {
  if (!itemData.value) {
    return;
  }

  itemData.value.body = itemData.value.body + ` [${mergeField}]`;
  characterCount.value = itemData.value.body.length;
};

const bodyChanged = (message: string) => {
  if (!message || typeof message != "string") {
    return;
  }

  characterCount.value = message.length;
};

const applyAIResponse = (aiText: string) => {
  if (!itemData.value) {
    return;
  }

  itemData.value.body = aiText;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(modalBase, {
      title: "SMS Text Template",
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((showModal).value = $event)),
      showCloseButton: true,
      showCloseConfirmationPrompt: displayCloseConfirmationPrompt.value,
      height: "410px",
      onCancelConfirmationModal: keepEnteredInformation,
      onModalClosed: modalClosed,
      width: _unref(ModalWidth).Small,
      onCancelPromptDenied: cancelPromptDenied
    }, {
      content: _withCtx(() => [
        (itemData.value)
          ? (_openBlock(), _createBlock(_unref(DxTabPanel), {
              key: 0,
              "scroll-by-content": true,
              "show-nav-buttons": true,
              "swipe-enabled": false,
              selectedIndex: selectedTab.value,
              "onUpdate:selectedIndex": _cache[9] || (_cache[9] = ($event: any) => ((selectedTab).value = $event))
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(DxItem), { title: "General" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                      _createVNode(AcquaintTextbox, {
                        fieldValidation: _unref(SMSTextsValidation).Description,
                        label: "Description",
                        modelValue: itemData.value.description,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((itemData.value.description) = $event)),
                        readOnly: lockedByAnotherUser.value,
                        validationGroup: "SMSTextsValidationGroup"
                      }, null, 8, ["fieldValidation", "modelValue", "readOnly"]),
                      _createElementVNode("div", _hoisted_2, [
                        _createVNode(LookupSingleSelect, {
                          fieldValidation: _unref(SMSTextsValidation).MailshotGroupID,
                          label: "Group",
                          modelValue: itemData.value.mailshotGroupID,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((itemData.value.mailshotGroupID) = $event)),
                          readOnly: lockedByAnotherUser.value,
                          validationGroup: "SMSTextsValidationGroup",
                          "lookup-source": _unref(LookupTableSource).TemplateGroups,
                          width: _unref(FieldSize).Large
                        }, null, 8, ["fieldValidation", "modelValue", "readOnly", "lookup-source", "width"]),
                        _createVNode(SystemCodeSingleSelect, {
                          "code-type": _unref(CodeType).ContentType,
                          fieldValidation: _unref(SMSTextsValidation).ContentTypeID,
                          label: "Content Type",
                          modelValue: itemData.value.contentTypeID,
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((itemData.value.contentTypeID) = $event)),
                          readOnly: lockedByAnotherUser.value,
                          validationGroup: "SMSTextsValidationGroup",
                          width: _unref(FieldSize).Large
                        }, null, 8, ["code-type", "fieldValidation", "modelValue", "readOnly", "width"]),
                        _createVNode(NumberDropdown, {
                          modelValue: itemData.value.followUpTaskInterval,
                          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((itemData.value.followUpTaskInterval) = $event)),
                          itemNumber: 360,
                          itemLabelExtension: "Days",
                          label: "Follow-up Task Interval",
                          readOnly: lockedByAnotherUser.value,
                          validationGroup: "SMSTextsValidationGroup",
                          fieldValidation: _unref(SMSTextsValidation).FollowUpTaskInterval
                        }, null, 8, ["modelValue", "readOnly", "fieldValidation"]),
                        _createVNode(SystemCodeSingleSelect, {
                          "code-type": _unref(CodeType).TaskPriority,
                          fieldValidation: _unref(SMSTextsValidation).FollowUpTaskPriority,
                          label: "Follow Up Task Priority",
                          modelValue: itemData.value.followUpTaskPriority,
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((itemData.value.followUpTaskPriority) = $event)),
                          readOnly: lockedByAnotherUser.value,
                          validationGroup: "SMSTextsValidationGroup",
                          width: _unref(FieldSize).Large
                        }, null, 8, ["code-type", "fieldValidation", "modelValue", "readOnly", "width"])
                      ]),
                      _createElementVNode("div", null, [
                        _createVNode(SwitchToggle, {
                          label: "Save As Correspondence",
                          modelValue: itemData.value.saveAsCorrespondence,
                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((itemData.value.saveAsCorrespondence) = $event)),
                          readOnly: lockedByAnotherUser.value,
                          labelWidth: "9.5rem"
                        }, null, 8, ["modelValue", "readOnly"]),
                        _createVNode(SwitchToggle, {
                          label: "Inactive",
                          labelWidth: "9.5rem",
                          modelValue: itemData.value.inActive,
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((itemData.value.inActive) = $event)),
                          readOnly: lockedByAnotherUser.value
                        }, null, 8, ["modelValue", "readOnly"])
                      ])
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DxItem), { title: "Message" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_3, [
                      _createVNode(AcquaintTextArea, {
                        fieldValidation: _unref(SMSTextsValidation).Body,
                        label: "Message",
                        "display-a-i-modal": true,
                        modelValue: itemData.value.body,
                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((itemData.value.body) = $event)),
                        readOnly: lockedByAnotherUser.value,
                        validationGroup: "SMSTextsValidationGroup",
                        onOnOptionChanged: bodyChanged,
                        onAiIconClick: _cache[8] || (_cache[8] = ($event: any) => (displayAIModal.value = true))
                      }, null, 8, ["fieldValidation", "modelValue", "readOnly"]),
                      _createElementVNode("div", _hoisted_4, [
                        _createVNode(AcquaintInfoBox, {
                          text: characterCount.value.toString(),
                          label: "Characters Typed",
                          width: _unref(FieldSize).Small
                        }, null, 8, ["text", "width"]),
                        _createVNode(AcquaintInfoBox, {
                          text: messageLengthWarning.value,
                          width: _unref(FieldSize).Medium
                        }, null, 8, ["text", "width"])
                      ])
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["selectedIndex"]))
          : _createCommentVNode("", true)
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "before",
          options: {
          text: 'Insert Field',
          onClick: () => {
            openMergeFieldsModal.value = true;
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Save',
          disabled: lockedByAnotherUser.value,
          onClick: (e) => {
            saveDetails(e);
          },
          validationGroup: 'SMSTextsValidationGroup',
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Cancel',
          onClick: () => {
            cancelClick();
          },
        }
        }, null, 8, ["options"])
      ]),
      _: 1
    }, 8, ["modelValue", "showCloseConfirmationPrompt", "width"]),
    _createVNode(AiTextGeneratorModal, {
      modelValue: displayAIModal.value,
      "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((displayAIModal).value = $event)),
      onApplyAIResponse: applyAIResponse,
      "display-apply-button": true
    }, null, 8, ["modelValue"]),
    _createVNode(MergeFieldsModal, {
      modelValue: openMergeFieldsModal.value,
      "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((openMergeFieldsModal).value = $event)),
      onAddMergeField: addMergeField
    }, null, 8, ["modelValue"])
  ], 64))
}
}

})